"use client";

import {
  Close,
  Content,
  DialogPortal,
  Overlay,
  Root,
  Title,
  Trigger,
} from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import clsx from "clsx";
import React from "react";

const FullScreenDialog = Root;
const FullScreenDialogTrigger = Trigger;
const FullScreenDialogClose = Close;
const FullScreenDialogTitle = Title;

type FullScreenDialogProps = {
  overlayClassNames?: string;
} & React.ComponentPropsWithoutRef<typeof Content>;

const FullScreenDialogContent = React.forwardRef<
  React.ElementRef<typeof Content>,
  FullScreenDialogProps
>(({ className, overlayClassNames, children, ...props }, ref) => (
  <DialogPortal>
    <Overlay className={overlayClassNames} />
    {/*
     * Do NOT wrap Content in an intermediate <div>: DialogPortal wraps every
     * direct child in <Presence>, which UNMOUNTs immediately when the element
     * has `animation-name: none`.
     *
     * Uses custom `dialog-fullscreen-*` keyframes (see tailwind-base.css)
     * instead of tailwindcss-animate: no centering translate needed for
     * fullscreen content, and v4's `translate:` property would stack with
     * tailwindcss-animate's `transform` keyframes.
     */}
    <Content
      ref={ref}
      className={clsx(
        "dialog-content-fullscreen fixed inset-0 z-50 flex w-full flex-col gap-4 bg-[white] p-6",
        className,
      )}
      {...props}
    >
      <VisuallyHidden>
        <FullScreenDialogTitle>Dialog</FullScreenDialogTitle>
      </VisuallyHidden>
      {children}
    </Content>
  </DialogPortal>
));

FullScreenDialogContent.displayName = Content.displayName;

export {
  FullScreenDialog,
  FullScreenDialogClose,
  FullScreenDialogContent,
  FullScreenDialogTitle,
  FullScreenDialogTrigger,
};
